import React, { useState, useEffect, useRef } from 'react';
import { api, apiFetch } from '../lib/api';
import { Card, Btn, SLabel } from '../components/ui';
import { usePeople } from '../context/PeopleContext';
import TravelTimeline from '../components/TravelTimeline';
import TravelDetailModal from '../components/TravelModal';
import { CalendarPicker, TimePicker } from './SheetBuilder';
import { useConfirm } from '../components/ConfirmModal';

function LocationSearch({ value, onChange }) {
  const [query, setQuery]     = React.useState(value || '');
  const [results, setResults] = React.useState([]);
  const [show, setShow]       = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const debounceRef           = React.useRef(null);

  const fetchPlaces = async (text) => {
    if (text.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const token   = localStorage.getItem('ds_token');
      const res     = await fetch(`${API_URL}/places/autocomplete?input=${encodeURIComponent(text)}`, {
        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await res.json();
      setResults(data.predictions || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    setShow(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPlaces(val), 300);
  };

  const select = (desc) => {
    setQuery(desc);
    onChange(desc);
    setShow(false);
    setResults([]);
  };

  return (
    <div style={{ position: "relative", zIndex: 50 }}>
      <input
        value={query}
        onChange={handleChange}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 200)}
        placeholder="Search any venue, city, or address..."
        style={{ width:"100%", background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px", fontSize:14, color:"var(--text)", outline:"none", boxSizing:"border-box" }}
      />
      {show && results.length > 0 && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, marginTop:4, overflow:"hidden", boxShadow:"0 8px 24px rgba(0,0,0,0.4)", zIndex:100 }}>
          {results.map((r, i) => (
            <button key={i} onMouseDown={() => select(r.description)}
              style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"11px 14px", background:"none", border:"none", borderBottom: i < results.length-1 ? "1px solid var(--border)" : "none", cursor:"pointer", textAlign:"left", fontFamily:"inherit" }}>
              <i className="fa-solid fa-location-dot" style={{ fontSize:14 }}></i>
              <span style={{ fontSize:13, color:"var(--text)", flex:1 }}>{r.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}



// Common-carrier short list — covers the airlines tour teams hit most.
// `code` is the IATA carrier prefix used on the Flight # field, so picking
// "Delta" can pre-fill "DL" if we ever extend the cascade. For now we just
// surface the brand name on pick.
const AIRLINES = [
  'Aer Lingus','Aeromexico','Air Canada','Air France','Alaska Airlines','Allegiant Air',
  'American Airlines','Avianca','British Airways','Cathay Pacific','China Eastern',
  'Copa Airlines','Delta','Emirates','Etihad Airways','EVA Air','Finnair','Frontier',
  'Hawaiian Airlines','Iberia','ITA Airways','JetBlue','KLM','Korean Air','LATAM',
  'Lufthansa','Norse Atlantic','Norwegian','Qantas','Qatar Airways','Ryanair',
  'SAS','Singapore Airlines','Southwest','Spirit','Sun Country','Swiss','Turkish Airlines',
  'United','Virgin Atlantic','Virgin Australia','WestJet',
];

// Datalist-backed input so typing "uni" surfaces "United". Plain text still
// works (admin can hand-type any carrier — short-haul charters, regional
// affiliates, etc.). Cheap, no API call.
function AirlineInput({ value, onChange, placeholder, isPrivate }) {
  const listId = React.useMemo(() => `airline-list-${Math.random().toString(36).slice(2,8)}`, []);
  return (
    <>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        list={isPrivate ? undefined : listId}
        style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }}
      />
      {!isPrivate && (
        <datalist id={listId}>
          {AIRLINES.map(a => <option key={a} value={a} />)}
        </datalist>
      )}
    </>
  );
}

// City autocomplete — same /places/autocomplete endpoint as LocationSearch
// but biased toward city-level results. Picking a result fills the input
// with "City, ST" / "City, Country". Manual typing always works.
function CitySearch({ value, onChange, onCommit, placeholder }) {
  const [query, setQuery] = React.useState(value || '');
  const [results, setResults] = React.useState([]);
  const [show, setShow] = React.useState(false);
  const debounceRef = React.useRef(null);
  const ref = React.useRef(null);
  const lastCommittedRef = React.useRef(value || '');

  React.useEffect(() => { setQuery(value || ''); }, [value]);

  // Fire onCommit when the field's value stabilizes — either after a
  // pick (immediate) or after blur (settled typed value). Lets the
  // parent run cascades like "city → airport" without over-firing on
  // every keystroke.
  const fireCommit = (text) => {
    if (!onCommit) return;
    if (lastCommittedRef.current === text) return;
    lastCommittedRef.current = text;
    onCommit(text);
  };

  const fetchCities = async (text) => {
    if (text.length < 2) { setResults([]); return; }
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const token = localStorage.getItem('ds_token');
      const res = await fetch(`${API_URL}/places/autocomplete?input=${encodeURIComponent(text)}`, {
        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await res.json();
      // Filter to results that look like cities — multi-segment ones
      // ("Hudson House, 4216 Oak Lawn Ave, Dallas, TX") usually aren't.
      // Keep predictions whose description is just 2 segments (City, ST).
      const cityish = (data.predictions || []).filter(p => {
        const segs = (p.description || '').split(',').map(s => s.trim());
        return segs.length >= 2 && segs.length <= 3;
      });
      setResults(cityish.slice(0, 8));
    } catch { setResults([]); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    setShow(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCities(val), 250);
  };

  const select = (prediction) => {
    const desc = prediction.description || '';
    setQuery(desc);
    onChange(desc);
    fireCommit(desc);
    setShow(false);
    setResults([]);
  };

  const handleBlur = () => {
    // Hide dropdown after a tick (so click on a result still fires)
    // and commit the current text — so manually-typed cities also
    // trigger the airport cascade.
    setTimeout(() => setShow(false), 150);
    fireCommit(query);
  };

  React.useEffect(() => {
    if (!show) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show]);

  return (
    <div ref={ref} style={{ position:'relative', zIndex:50 }}>
      <input
        value={query}
        onChange={handleChange}
        onFocus={() => setShow(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }}
      />
      {show && results.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, marginTop:4, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.4)', zIndex:100, maxHeight:220, overflowY:'auto' }}>
          {results.map((r, i) => (
            <button key={i} onMouseDown={() => select(r)}
              style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 14px', background:'none', border:'none', borderBottom: i < results.length-1 ? '1px solid var(--border)' : 'none', cursor:'pointer', textAlign:'left', fontFamily:'inherit', color:'var(--text)', fontSize:13 }}>
              <i className="fa-solid fa-location-dot" style={{ fontSize:11, color:'var(--text3)' }}></i>
              {r.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AirportSearch({ value, onChange, placeholder }) {
  const [query, setQuery] = React.useState(value || '');
  const [results, setResults] = React.useState([]);
  const [show, setShow] = React.useState(false);
  const debounceRef = React.useRef(null);
  const ref = React.useRef(null);

  React.useEffect(() => { setQuery(value || ''); }, [value]);

  const fetchAirports = async (text) => {
    if (text.length < 2) { setResults([]); return; }
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const token = localStorage.getItem('ds_token');
      const res = await fetch(`${API_URL}/travel/airports?q=${encodeURIComponent(text)}`, {
        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await res.json();
      setResults(data || []);
    } catch { setResults([]); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val.toUpperCase());
    setShow(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchAirports(val), 200);
  };

  const select = (airport) => {
    const code = airport.icao.startsWith('K') && airport.iata ? airport.iata : airport.icao;
    setQuery(code);
    // Forward the full airport record alongside the code so callers
    // can cascade — e.g. Travel.jsx auto-fills the matching City field
    // when an airport is picked. Older callers ignoring the second
    // arg keep working unchanged.
    onChange(code, airport);
    setShow(false);
    setResults([]);
  };

  React.useEffect(() => {
    if (!show) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show]);

  return (
    <div ref={ref} style={{ position: 'relative', zIndex: 50 }}>
      <input
        value={query}
        onChange={handleChange}
        onFocus={() => { setShow(true); if (query.length >= 2) fetchAirports(query); }}
        placeholder={placeholder || 'Search airport...'}
        style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', textTransform:'uppercase', fontWeight:700, letterSpacing:0.5 }}
      />
      {show && results.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, marginTop:4, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.4)', zIndex:100, maxHeight:220, overflowY:'auto' }}>
          {results.map((a, i) => (
            <button key={a.icao+i} onMouseDown={() => select(a)}
              style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 14px', background:'none', border:'none', borderBottom: i < results.length-1 ? '1px solid var(--border)' : 'none', cursor:'pointer', textAlign:'left', fontFamily:'inherit' }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, minWidth:70 }}>
                <span style={{ fontSize:14, fontWeight:800, color:'var(--text)', fontFamily:'monospace' }}>{a.iata || a.icao}</span>
                {a.iata && a.icao !== 'K'+a.iata && (
                  <span style={{ fontSize:10, color:'var(--text3)', fontFamily:'monospace' }}>{a.icao}</span>
                )}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <span style={{ fontSize:12, color:'var(--text2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block' }}>
                  {a.name}
                </span>
                <span style={{ fontSize:10, color:'var(--text3)' }}>
                  {a.city}{a.state ? ', '+a.state : ''}{a.country && a.country !== 'US' ? ' · '+a.country : ''}
                </span>
              </div>
              {a.private && <span style={{ fontSize:8, fontWeight:700, color:'#a78bfa', background:'rgba(167,139,250,0.1)', border:'1px solid rgba(167,139,250,0.2)', borderRadius:4, padding:'2px 5px' }}>GA</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TRAVEL_TYPES = [
  { id: 'flight',     label: 'Flight',        icon: 'fa-plane' },
  { id: 'hotel',      label: 'Hotel',         icon: 'fa-hotel' },
  { id: 'carservice', label: 'Car Service',   icon: 'fa-car-side' },
  { id: 'car',        label: 'Car Rental',    icon: 'fa-car' },
  { id: 'ground',     label: 'Ground',        icon: 'fa-van-shuttle' },
  { id: 'train',      label: 'Train',         icon: 'fa-train' },
];

// Hotel-name autocomplete. Hits Google Places via the existing
// /places/autocomplete proxy and, on pick, splits the prediction
// description into name (first comma-segment) and address (rest) so
// the name and address fields stay clean and parseable downstream.
// Same pattern SheetLab's VenueModal uses for venue lookup.
function HotelNameSearch({ name, onName, onPick, inp }) {
  const [predictions, setPredictions] = React.useState([]);
  const [show, setShow] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const debounceRef = React.useRef(null);

  function search(text) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.length < 2) { setPredictions([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.placesAuto(text);
        setPredictions(res?.predictions || []);
      } catch { setPredictions([]); }
      finally { setLoading(false); }
    }, 300);
  }

  function pick(prediction) {
    // "Faena Hotel Buenos Aires, Martha Salotti 445, Buenos Aires, Argentina"
    //   → name: "Faena Hotel Buenos Aires"
    //   → address: "Martha Salotti 445, Buenos Aires, Argentina"
    const desc = prediction.description || '';
    const idx = desc.indexOf(',');
    const splitName = idx === -1 ? desc : desc.slice(0, idx).trim();
    const splitAddr = idx === -1 ? '' : desc.slice(idx + 1).trim();
    // Capture placeId so the iOS hotel detail card can later enrich
    // with photos / website / phone via /places/details. Falls back to
    // null when the autocomplete response was older / didn't include it.
    onPick({ name: splitName, address: splitAddr, placeId: prediction.placeId || null });
    setPredictions([]);
    setShow(false);
  }

  return (
    <div style={{ position: 'relative', zIndex: 50 }}>
      <input
        value={name}
        onChange={e => { onName(e.target.value); search(e.target.value); setShow(true); }}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 200)}
        placeholder="The Standard Hotel"
        style={inp}
      />
      {show && predictions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 9999,
        }}>
          {predictions.map((p, i) => (
            <div
              key={i}
              onMouseDown={() => pick(p)}
              style={{
                padding: '11px 14px', cursor: 'pointer', fontSize: 14,
                color: 'var(--text)',
                borderBottom: i < predictions.length - 1 ? '1px solid var(--border)' : 'none',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <i className="fa-solid fa-hotel" style={{ fontSize: 12, color: '#06B6D4' }}></i>
              {p.description}
            </div>
          ))}
        </div>
      )}
      {loading && (
        <div style={{ position: 'absolute', right: 12, top: 14 }}>
          <div style={{ width: 14, height: 14, border: '2px solid var(--border)', borderTop: '2px solid var(--blue)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}
    </div>
  );
}

function PassengerPicker({ value, onChange, artistId, artists }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Parse comma-separated value into array of selected names
  const selected = (value || '').split(',').map(s => s.trim()).filter(Boolean);

  useEffect(() => {
    const build = async () => {
      const names = [];
      if (artists?.length) {
        artists.forEach(a => {
          names.push(a.name);
          if (a.aliases) a.aliases.split(',').map(al => al.trim()).filter(Boolean).forEach(al => names.push(al));
        });
      }
      try {
        const team = await apiFetch(`/artists/${artistId}/team`);
        if (Array.isArray(team)) team.forEach(m => { if (m.name) names.push(m.name); });
      } catch {}
      setSuggestions([...new Set(names)]);
    };
    if (artistId) build();
  }, [artistId, artists]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const addName = (name) => {
    if (selected.some(s => s.toLowerCase() === name.toLowerCase())) return;
    const next = [...selected, name].join(', ');
    onChange(next);
    setQuery('');
  };

  const removeName = (name) => {
    const next = selected.filter(s => s.toLowerCase() !== name.toLowerCase()).join(', ');
    onChange(next);
  };

  const filtered = suggestions.filter(s =>
    !selected.some(sel => sel.toLowerCase() === s.toLowerCase()) &&
    (!query || s.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div ref={ref} style={{ position:'relative' }}>
      {/* Selected chips + input */}
      <div onClick={() => inputRef.current?.focus()}
        style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center',
          background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10,
          padding:'8px 12px', minHeight:42, cursor:'text', boxSizing:'border-box' }}>
        {selected.map(name => (
          <span key={name} style={{ display:'inline-flex', alignItems:'center', gap:5,
            padding:'4px 8px 4px 10px', borderRadius:8, background:'rgba(79,156,249,0.12)',
            border:'1px solid rgba(79,156,249,0.25)', fontSize:13, fontWeight:600, color:'var(--blue)' }}>
            {name}
            <button onClick={e => { e.stopPropagation(); removeName(name); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)',
                fontSize:14, lineHeight:1, padding:0, fontFamily:'inherit', display:'flex' }}>
              <i className="fa-solid fa-xmark" style={{ fontSize:10 }}></i>
            </button>
          </span>
        ))}
        <input ref={inputRef} value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Backspace' && !query && selected.length) removeName(selected[selected.length - 1]);
            if (e.key === 'Enter' && query.trim()) { e.preventDefault(); addName(query.trim()); }
          }}
          placeholder={selected.length ? '+ add another...' : 'Who is this for? Select one or many...'}
          style={{ flex:1, minWidth:80, background:'none', border:'none', outline:'none',
            fontSize:14, color:'var(--text)', padding:'2px 0' }} />
      </div>
      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:100, marginTop:4,
          background:'var(--card)', border:'1px solid var(--border)', borderRadius:10,
          overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.4)', maxHeight:200, overflowY:'auto' }}>
          {filtered.map((name, i) => (
            <button key={i} onMouseDown={() => addName(name)}
              style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 14px',
                background:'none', border:'none', borderBottom: i < filtered.length-1 ? '1px solid var(--border)' : 'none',
                cursor:'pointer', textAlign:'left', fontFamily:'inherit' }}>
              <i className="fa-solid fa-user" style={{ fontSize:12, color:'var(--blue)' }}></i>
              <span style={{ fontSize:13, color:'var(--text)', fontWeight:600 }}>{name}</span>
              {artists?.some(a => a.name === name) && (
                <span style={{ fontSize:10, color:'var(--text3)', marginLeft:'auto' }}>Artist</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function TravelFormModal({ record, artistId, artists, onSave, onClose, defaultDate, lockedType }) {
  const isEdit = !!record?.id;
  const [type,setType] = useState(record?.type||'flight');
  const dateDefault = defaultDate || new Date().toISOString().slice(0,10);
  const [startDate,setStartDate] = useState(record?.startDate||dateDefault);
  const [endDate,setEndDate] = useState(record?.endDate||dateDefault);
  const [hotelName,setHotelName] = useState(record?.hotelName||'');
  const [hotelAddress,setHotelAddress] = useState(record?.hotelAddress||'');
  const [hotelConfirmation,setHotelConfirmation] = useState(record?.hotelConfirmation||'');
  const [roomType,setRoomType] = useState(record?.roomType||'');
  // Captured from the HotelNameSearch autocomplete pick — drives the
  // iOS hotel detail card's Places enrichment (photos / website / phone).
  // Cleared if the user manually retypes the name so we don't keep a
  // stale placeId pointing at a different hotel.
  const [hotelPlaceId,setHotelPlaceId] = useState(record?.placeId||null);
  const [carCompany,setCarCompany] = useState(record?.carCompany||'');
  const [carPickup,setCarPickup] = useState(record?.carPickupLocation||'');
  const [carDropoff,setCarDropoff] = useState(record?.carDropoffLocation||'');
  const [carConfirmation,setCarConfirmation] = useState(record?.carConfirmation||'');
  const [driverName,setDriverName] = useState(record?.driverName||'');
  const [driverPhone,setDriverPhone] = useState(record?.driverPhone||'');
  const [pickupLocation,setPickupLocation] = useState(record?.pickupLocation||'');
  const [dropoffLocation,setDropoffLocation] = useState(record?.dropoffLocation||'');
  const [trainCompany,setTrainCompany] = useState(record?.trainCompany||'');
  const [trainFrom,setTrainFrom] = useState(record?.trainFrom||'');
  const [trainTo,setTrainTo] = useState(record?.trainTo||'');
  const [trainConfirmation,setTrainConfirmation] = useState(record?.trainConfirmation||'');
  const [departureTime,setDepartureTime] = useState(record?.departureTime||'');
  const [arrivalTime,setArrivalTime] = useState(record?.arrivalTime||'');
  const [airline,setAirline] = useState(record?.airline||'');
  const [flightNumber,setFlightNumber] = useState(record?.flightNumber||'');
  const [departureAirport,setDepartureAirport] = useState(record?.departureAirport||'');
  const [arrivalAirport,setArrivalAirport] = useState(record?.arrivalAirport||'');
  // Track the airport's local timezone so we can label the time inputs
  // — admins routinely think in their sheet's zone and would otherwise
  // type "12:20 PM" for a Vegas landing meaning ET, not the airport-local
  // PT the boarding pass shows. Seeded from the saved record when
  // editing; updated on every airport pick / city cascade.
  const [departureTz,setDepartureTz] = useState(record?.departureTz||'');
  const [arrivalTz,setArrivalTz] = useState(record?.arrivalTz||'');
  const [departureCity,setDepartureCity] = useState(record?.departureCity||'');
  const [arrivalCity,setArrivalCity] = useState(record?.arrivalCity||'');
  const [flightDepartureTime,setFlightDepartureTime] = useState(record?.flightDepartureTime||'');
  const [flightArrivalTime,setFlightArrivalTime] = useState(record?.flightArrivalTime||'');
  const [flightDuration,setFlightDuration] = useState(record?.flightDuration||'');
  const [cabinClass,setCabinClass] = useState(record?.cabinClass||'');
  const [seat,setSeat] = useState(record?.seat||'');
  const [flightConfirmation,setFlightConfirmation] = useState(record?.flightConfirmation||'');
  const [passenger,setPassenger] = useState(record?.passenger||'');
  const [leadPassenger,setLeadPassenger] = useState(record?.leadPassenger||'');
  const [greeterName,setGreeterName] = useState(record?.greeterName||'');
  const [greeterPhone,setGreeterPhone] = useState(record?.greeterPhone||'');
  const [departureGreeterName,setDepartureGreeterName] = useState(record?.departureGreeterName||'');
  const [departureGreeterPhone,setDepartureGreeterPhone] = useState(record?.departureGreeterPhone||'');
  const [departureDrivers,setDepartureDrivers] = useState(record?.departureDrivers?.length ? record.departureDrivers : []);
  const [arrivalDrivers,setArrivalDrivers] = useState(record?.arrivalDrivers?.length ? record.arrivalDrivers : []);
  const [flightType,setFlightType] = useState(record?.flightType||'commercial');
  const [tailNumber,setTailNumber] = useState(record?.tailNumber||'');
  const [aircraftType,setAircraftType] = useState(record?.aircraftType||'');
  const [operatorName,setOperatorName] = useState(record?.operator||'');
  const [departureFBO,setDepartureFBO] = useState(record?.departureFBO||'');
  const [departureFBOPhone,setDepartureFBOPhone] = useState(record?.departureFBOPhone||'');
  const [departureFBOAddress,setDepartureFBOAddress] = useState(record?.departureFBOAddress||'');
  const [arrivalFBO,setArrivalFBO] = useState(record?.arrivalFBO||'');
  const [arrivalFBOPhone,setArrivalFBOPhone] = useState(record?.arrivalFBOPhone||'');
  const [arrivalFBOAddress,setArrivalFBOAddress] = useState(record?.arrivalFBOAddress||'');
  const [requestedBy,setRequestedBy] = useState(record?.requestedBy||'');
  const [requestedByPhone,setRequestedByPhone] = useState(record?.requestedByPhone||'');
  const [createEvents,setCreateEvents] = useState(false);
  const [carServiceCompany,setCarServiceCompany] = useState(record?.carServiceCompany||'');
  const [carServicePhone,setCarServicePhone] = useState(record?.carServicePhone||'');
  const [carServicePickup,setCarServicePickup] = useState(record?.carServicePickup||'');
  const [carServiceDropoff,setCarServiceDropoff] = useState(record?.carServiceDropoff||'');
  const [carServiceDriver,setCarServiceDriver] = useState(record?.carServiceDriver||'');
  const [carServiceConfirmation,setCarServiceConfirmation] = useState(record?.carServiceConfirmation||'');
  const [carServiceTime,setCarServiceTime] = useState(record?.carServiceTime||'');
  const [notes,setNotes] = useState(record?.notes||'');
  const [saving,setSaving] = useState(false);
  const [error,setError] = useState('');

  const inp = { width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' };

  // Hydrate tz tags for legacy flight records that don't carry
  // departureTz / arrivalTz on disk. Hits the server's single-airport
  // lookup once per code so the form labels light up immediately when
  // an old record is opened for edit.
  React.useEffect(() => {
    if (type !== 'flight') return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('ds_token');
    const lookup = async (code) => {
      try {
        const r = await fetch(`${API_URL}/travel/airport-tz?code=${encodeURIComponent(code)}`, {
          headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
        });
        const d = await r.json();
        return d?.tz || '';
      } catch { return ''; }
    };
    if (!departureTz && departureAirport) lookup(departureAirport).then(tz => tz && setDepartureTz(tz));
    if (!arrivalTz && arrivalAirport) lookup(arrivalAirport).then(tz => tz && setArrivalTz(tz));
  }, [type, departureAirport, arrivalAirport, departureTz, arrivalTz]);

  // Belt-and-suspenders city → airport cascade: re-derive airport
  // whenever the city state changes, regardless of how it got there
  // (typed, picked, Lookup-filled, edit-record-rehydration). The
  // CitySearch onCommit fires on pick + blur; this useEffect catches
  // every other path (debounced 400ms so it doesn't fire on every
  // keystroke). Only overrides when the metro hub doesn't already
  // match — leaves manual choices like "Newark → LAX" alone.
  React.useEffect(() => {
    if (type !== 'flight') return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('ds_token');
    const cascade = async (cityValue, currentAirport, setAirport, setTz) => {
      const cityName = (cityValue || '').split(',')[0].trim();
      if (cityName.length < 3) return;
      try {
        const r = await fetch(`${API_URL}/travel/airports?q=${encodeURIComponent(cityName)}`, {
          headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
        });
        const data = await r.json();
        // Only auto-replace when the current airport isn't in the
        // city's match set — prevents stomping on a deliberate choice
        // (e.g. flying out of LGA from "New York" instead of JFK).
        const matches = (data || []).map(a => (a.iata || a.icao || '').toUpperCase());
        if (!matches.length) return;
        const top = data[0];
        const cur = (currentAirport || '').toUpperCase();
        if (cur && matches.includes(cur)) {
          // Current airport is a valid match for this city — keep it,
          // but still backfill tz tag from the matching record.
          const match = data.find(a => (a.iata || a.icao || '').toUpperCase() === cur);
          if (match?.tz) setTz(match.tz);
          return;
        }
        setAirport(top.iata || top.icao);
        if (top.tz) setTz(top.tz);
      } catch { /* non-fatal */ }
    };
    const t = setTimeout(() => {
      cascade(departureCity, departureAirport, setDepartureAirport, setDepartureTz);
      cascade(arrivalCity,   arrivalAirport,   setArrivalAirport,   setArrivalTz);
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [type, departureCity, arrivalCity]);

  const handleSave = async (pushGCal = false) => {
    if (!startDate||!endDate) { setError('Start and end dates are required'); return; }
    setSaving(true); setError('');
    try {
      const body = { artistId, type, startDate, endDate, notes:notes||null,
        hotelName:hotelName||null, hotelAddress:hotelAddress||null, hotelConfirmation:hotelConfirmation||null, roomType:roomType||null,
        // placeId only meaningful for hotel records right now, but we send
        // it for any type — server stores it generically; non-hotel types
        // simply leave it null and ignore.
        placeId: type==='hotel' ? (hotelPlaceId||null) : null,
        carCompany:carCompany||null, carPickupLocation:carPickup||null, carDropoffLocation:carDropoff||null, carConfirmation:carConfirmation||null,
        driverName:driverName||null, driverPhone:driverPhone||null, pickupLocation:pickupLocation||null, dropoffLocation:dropoffLocation||null,
        trainCompany:trainCompany||null, trainFrom:trainFrom||null, trainTo:trainTo||null, trainConfirmation:trainConfirmation||null,
        departureTime:departureTime||null, arrivalTime:arrivalTime||null,
        flightType:flightType||'commercial',
        airline:airline||null, flightNumber:flightNumber||null, departureAirport:departureAirport||null, arrivalAirport:arrivalAirport||null,
        departureCity:departureCity||null, arrivalCity:arrivalCity||null,
        flightDepartureTime:flightDepartureTime||null, flightArrivalTime:flightArrivalTime||null,
        flightDuration:flightDuration||null, cabinClass:cabinClass||null, seat:seat||null,
        flightConfirmation:flightConfirmation||null,
        passenger: (() => {
          if (!passenger || !leadPassenger) return passenger || null;
          const pax = passenger.split(',').map(p => p.trim()).filter(Boolean);
          const li = pax.findIndex(p => p.toLowerCase() === leadPassenger.toLowerCase());
          if (li > 0) { const [lp] = pax.splice(li, 1); pax.unshift(lp); }
          return pax.join(', ');
        })(),
        leadPassenger:leadPassenger||null,
        greeterName:greeterName||null, greeterPhone:greeterPhone||null,
        departureGreeterName:departureGreeterName||null, departureGreeterPhone:departureGreeterPhone||null,
        departureDrivers:departureDrivers.filter(d=>d.name), arrivalDrivers:arrivalDrivers.filter(d=>d.name),
        tailNumber:tailNumber||null, aircraftType:aircraftType||null, operator:operatorName||null,
        departureFBO:departureFBO||null, departureFBOPhone:departureFBOPhone||null, departureFBOAddress:departureFBOAddress||null,
        arrivalFBO:arrivalFBO||null, arrivalFBOPhone:arrivalFBOPhone||null, arrivalFBOAddress:arrivalFBOAddress||null,
        requestedBy:requestedBy||null, requestedByPhone:requestedByPhone||null,
        carServiceCompany:carServiceCompany||null, carServicePhone:carServicePhone||null,
        carServicePickup:carServicePickup||null, carServiceDropoff:carServiceDropoff||null,
        carServiceDriver:carServiceDriver||null, carServiceConfirmation:carServiceConfirmation||null,
        carServiceTime:carServiceTime||null };
      const result = isEdit ? await api.updateTravel(record.id,body) : await api.createTravel(body);

      // Push auto-created events to Google Calendar if requested
      if (pushGCal && result.events?.length) {
        try {
          for (const evt of result.events) {
            if (evt.id) await api.pushToGCal(evt.id);
          }
        } catch(e) { console.error('GCal push failed:', e); }
      }

      onSave(result);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onMouseDown={e => e.target===e.currentTarget && onClose()}>
      <button onClick={onClose}
        style={{ position:'absolute', top:16, left:16, width:36, height:36, borderRadius:'50%',
          background:'rgba(255,255,255,0.12)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
          border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2010 }}>
        <i className="fa-solid fa-xmark" style={{ fontSize:16, color:'#fff' }}></i>
      </button>
      <div style={{ background:'var(--card)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:560, padding:'28px 28px calc(28px + env(safe-area-inset-bottom, 0px))', maxHeight:'92vh', overflowY:'auto', border:'1px solid var(--border)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <h3 style={{ fontSize:20, fontWeight:800, color:'var(--text)' }}>{isEdit?'Edit':'Add'} Travel</h3>
          <button onClick={onClose} style={{ fontSize:22, color:'var(--text3)', background:'none', border:'none', cursor:'pointer' }}>x</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Type selector. When `lockedType` is set (e.g. opened from a
             "+ New flight" CTA in the sheet builder), only render that
             one option so the user can't accidentally pivot to a
             different travel kind mid-form. */}
          {!lockedType && (
            <div>
              <SLabel>Type</SLabel>
              <div style={{ display:'flex', gap:8 }}>
                {TRAVEL_TYPES.map(t => (
                  <button key={t.id} onClick={() => setType(t.id)}
                    style={{ flex:1, padding:'10px 4px', borderRadius:10, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700,
                      background:type===t.id?'rgba(79,156,249,0.15)':'var(--bg)', borderColor:type===t.id?'var(--blue)':'var(--border)', color:type===t.id?'var(--blue)':'var(--text2)' }}>
                    <i className={`fa-solid ${t.icon}`}></i><br/>{t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div><SLabel>{type==='hotel' ? 'Check-in' : 'Start Date'}</SLabel><CalendarPicker value={startDate} onChange={setStartDate} compact /></div>
            <div><SLabel>{type==='hotel' ? 'Check-out' : 'End Date'}</SLabel><CalendarPicker value={endDate} onChange={setEndDate} compact /></div>
          </div>
          <div><SLabel>Passenger / For</SLabel><PassengerPicker value={passenger} onChange={setPassenger} artistId={artistId} artists={artists} /></div>
          {/* Lead passenger selector — only show when multiple passengers */}
          {type === 'flight' && passenger && passenger.includes(',') && (() => {
            const pax = passenger.split(',').map(p => p.trim()).filter(Boolean);
            const lead = leadPassenger || pax[0];
            return (
              <div style={{ marginTop:-4, marginBottom:8 }}>
                <SLabel>Lead Passenger</SLabel>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {pax.map(p => (
                    <button key={p} onClick={() => setLeadPassenger(p)}
                      style={{ fontSize:12, fontWeight:700, padding:'5px 10px', borderRadius:8, border: (lead === p) ? '1.5px solid var(--blue)' : '1px solid var(--border)',
                        background: (lead === p) ? 'rgba(30,64,175,0.12)' : 'var(--bg)', color: (lead === p) ? 'var(--blue)' : 'var(--text2)',
                        cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:4 }}>
                      {(lead === p) && <i className="fa-solid fa-star" style={{ fontSize:9 }}></i>}
                      {p.split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          {type==='hotel' && <>
            <div>
              <SLabel>Hotel Name</SLabel>
              <HotelNameSearch
                name={hotelName}
                onName={(v) => {
                  setHotelName(v);
                  // Manual edit invalidates a previously-picked placeId —
                  // the user is no longer pointing at the same Google
                  // Places entity, so let them retype clean.
                  if (hotelPlaceId) setHotelPlaceId(null);
                }}
                onPick={({ name, address, placeId }) => {
                  setHotelName(name);
                  // Picking a hotel always rewrites the address — the
                  // autocomplete prediction is the canonical Google
                  // Places address for this hotel, which the user is
                  // free to fine-tune manually after.
                  setHotelAddress(address);
                  setHotelPlaceId(placeId || null);
                }}
                inp={inp}
              />
            </div>
            {/* Address is the auto-filled companion to the name search.
                Plain input — no second autocomplete — so there's only
                one smart-search affordance per form. Users can still
                hand-edit the line for a suite number / floor / fix to
                the picked address. */}
            <div><SLabel>Address</SLabel><input
              value={hotelAddress}
              onChange={e => setHotelAddress(e.target.value)}
              placeholder="Auto-fills from the hotel search above"
              style={inp}
            /></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Confirmation</SLabel><input value={hotelConfirmation} onChange={e=>setHotelConfirmation(e.target.value)} style={inp} /></div>
              <div><SLabel>Room Type</SLabel><input value={roomType} onChange={e=>setRoomType(e.target.value)} placeholder="King Suite" style={inp} /></div>
            </div>
          </>}
          {type==='car' && <>
            <div><SLabel>Rental Company</SLabel><input value={carCompany} onChange={e=>setCarCompany(e.target.value)} placeholder="Hertz, Avis..." style={inp} /></div>
            <div><SLabel>Pickup Location</SLabel><LocationSearch value={carPickup} onChange={setCarPickup} /></div>
            <div><SLabel>Dropoff Location</SLabel><LocationSearch value={carDropoff} onChange={setCarDropoff} /></div>
            <div><SLabel>Confirmation</SLabel><input value={carConfirmation} onChange={e=>setCarConfirmation(e.target.value)} style={inp} /></div>
          </>}
          {type==='ground' && <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Driver Name</SLabel><input value={driverName} onChange={e=>setDriverName(e.target.value)} style={inp} /></div>
              <div><SLabel>Driver Phone</SLabel><input value={driverPhone} onChange={e=>setDriverPhone(e.target.value)} style={inp} /></div>
            </div>
            <div><SLabel>Pickup Location</SLabel><LocationSearch value={pickupLocation} onChange={setPickupLocation} /></div>
            <div><SLabel>Dropoff Location</SLabel><LocationSearch value={dropoffLocation} onChange={setDropoffLocation} /></div>
          </>}
          {type==='flight' && <>
            {/* Commercial / Private toggle */}
            <div style={{ display:'flex', gap:0, borderRadius:10, overflow:'hidden', border:'1px solid var(--border)' }}>
              {[{id:'commercial',label:'Commercial',icon:'fa-plane'},{id:'private',label:'Private Aviation',icon:'fa-jet-fighter'}].map(ft => (
                <button key={ft.id} onClick={() => setFlightType(ft.id)}
                  style={{ flex:1, padding:'10px', background: flightType===ft.id ? 'rgba(79,156,249,0.12)' : 'var(--bg)',
                    border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700,
                    color: flightType===ft.id ? 'var(--blue)' : 'var(--text3)',
                    borderRight: ft.id==='commercial' ? '1px solid var(--border)' : 'none', transition:'all 0.15s' }}>
                  <i className={`fa-solid ${ft.icon}`} style={{ marginRight:6, fontSize:11 }}></i>{ft.label}
                </button>
              ))}
            </div>

            {flightType === 'commercial' && (
              <div style={{ background:'var(--bg)', borderRadius:10, padding:'12px 14px', border:'1px solid var(--border)' }}>
                <p style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', marginBottom:8, textTransform:'uppercase' }}>
                  <i className="fa-solid fa-magnifying-glass" style={{ marginRight:4, fontSize:9 }}></i> Quick Lookup
                </p>
                <div style={{ display:'flex', gap:8 }}>
                  <input value={flightNumber} onChange={e=>setFlightNumber(e.target.value.toUpperCase())} placeholder="UA1234"
                    style={{ ...inp, flex:1, fontSize:15, fontWeight:700, fontFamily:'monospace', letterSpacing:1 }}
                    onKeyDown={e => { if (e.key === 'Enter') document.getElementById('flight-lookup-btn')?.click(); }} />
                  <button id="flight-lookup-btn" onClick={async () => {
                    if (!flightNumber) return;
                    setError('');
                    const btn = document.getElementById('flight-lookup-btn');
                    if (btn) btn.textContent = 'Looking up...';
                    try {
                      const data = await api.flightLookup(flightNumber, startDate || undefined);
                      if (data.airline) setAirline(data.airline);
                      if (data.flightNumber) setFlightNumber(data.flightNumber);
                      if (data.departureAirport) setDepartureAirport(data.departureAirport);
                      if (data.arrivalAirport) setArrivalAirport(data.arrivalAirport);
                      if (data.flightDepartureTime) setFlightDepartureTime(data.flightDepartureTime);
                      if (data.flightArrivalTime) setFlightArrivalTime(data.flightArrivalTime);
                      if (data.startDate && !startDate) setStartDate(data.startDate);
                      if (data.endDate) setEndDate(data.endDate);
                      const notesParts = [];
                      if (data.terminal) notesParts.push('Terminal ' + data.terminal);
                      if (data.gate) notesParts.push('Gate ' + data.gate);
                      if (data.status) notesParts.push('Status: ' + data.status);
                      if (notesParts.length && !notes) setNotes(notesParts.join(' / '));
                    } catch(e) { setError('Lookup: ' + e.message); }
                    finally { if (btn) btn.innerHTML = '<i class="fa-solid fa-search" style="margin-right:4px;font-size:11px"></i> Lookup'; }
                  }}
                    style={{ padding:'10px 16px', borderRadius:10, background:'var(--blue)', border:'none', color:'white', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, flexShrink:0 }}>
                    <i className="fa-solid fa-search" style={{ marginRight:4, fontSize:11 }}></i> Lookup
                  </button>
                </div>
                <p style={{ fontSize:10, color:'var(--text3)', marginTop:6 }}>Enter flight number to auto-fill details</p>
              </div>
            )}

            {flightType === 'private' && (
              <div style={{ background:'rgba(167,139,250,0.04)', borderRadius:10, padding:'12px 14px', border:'1px solid rgba(167,139,250,0.2)' }}>
                <p style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, color:'#a78bfa', marginBottom:8, textTransform:'uppercase' }}>
                  <i className="fa-solid fa-jet-fighter" style={{ marginRight:4, fontSize:9 }}></i> Aircraft
                </p>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <div style={{ flex:1 }}>
                    <SLabel>Tail Number</SLabel>
                    <input value={tailNumber} onChange={e=>setTailNumber(e.target.value.toUpperCase())} placeholder="N154EA"
                      style={{ ...inp, fontFamily:'monospace', fontWeight:700, letterSpacing:1, fontSize:16 }}
                      onKeyDown={e => { if (e.key === 'Enter' && tailNumber) document.getElementById('tail-lookup-btn')?.click(); }} />
                  </div>
                  <div style={{ display:'flex', alignItems:'flex-end' }}>
                    <button id="tail-lookup-btn" onClick={async () => {
                      if (!tailNumber) return;
                      const btn = document.getElementById('tail-lookup-btn');
                      if (btn) btn.textContent = '...';
                      try {
                        const data = await api.tailLookup(tailNumber);
                        if (data.aircraftType) setAircraftType(data.aircraftType);
                        if (data.operator) setOperatorName(data.operator);
                        else if (data.owner) setOperatorName(data.owner);
                      } catch(e) { setError('Tail lookup: ' + e.message); }
                      finally { if (btn) btn.innerHTML = '<i class="fa-solid fa-search" style="margin-right:4px;font-size:10px"></i>Lookup'; }
                    }}
                      style={{ padding:'10px 14px', borderRadius:10, background:'#a78bfa', border:'none', color:'white', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                      <i className="fa-solid fa-search" style={{ marginRight:4, fontSize:10 }}></i>Lookup
                    </button>
                  </div>
                </div>
                <div><SLabel>Aircraft Type</SLabel><input value={aircraftType} onChange={e=>setAircraftType(e.target.value)} placeholder="Citation Latitude" style={inp} /></div>
                <div style={{ marginTop:8 }}><SLabel>Operator</SLabel><input value={operatorName} onChange={e=>setOperatorName(e.target.value)} placeholder="NetJets, Privé Jets..." style={inp} /></div>
              </div>
            )}

            {/* Airline alone — flight # already lives in the Quick
                Lookup field above. Keeping it duplicated here just made
                two inputs that always agree. */}
            <div>
              <SLabel>{flightType==='commercial'?'Airline':'Charter Company'}</SLabel>
              <AirlineInput value={airline} onChange={setAirline} placeholder={flightType==='commercial'?"United, Delta...":"NetJets, XO..."} isPrivate={flightType==='private'} />
            </div>
            {/* City-first ordering: pick the city, the airport code
                pre-fills from it. Airport stays editable for the
                multi-airport cities (NYC: JFK/LGA/EWR; LA: LAX/BUR). */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Departing City</SLabel><CitySearch
                value={departureCity}
                onChange={setDepartureCity}
                onCommit={async (desc) => {
                  // Cascade fires whenever the city value settles —
                  // either after picking from the dropdown or after
                  // blurring out of the field with a typed value.
                  // Top match is the major hub for the metro (LAX for
                  // LA, JFK for NYC, EWR for Newark, LAS for Vegas).
                  const cityName = (desc || '').split(',')[0].trim();
                  if (cityName.length < 3) return;
                  try {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                    const token = localStorage.getItem('ds_token');
                    const r = await fetch(`${API_URL}/travel/airports?q=${encodeURIComponent(cityName)}`, {
                      headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
                    });
                    const data = await r.json();
                    const top = (data || [])[0];
                    if (top) {
                      setDepartureAirport(top.iata || top.icao);
                      if (top.tz) setDepartureTz(top.tz);
                    }
                  } catch { /* non-fatal */ }
                }}
                placeholder="Newark, NJ" /></div>
              <div><SLabel>Arrival City</SLabel><CitySearch
                value={arrivalCity}
                onChange={setArrivalCity}
                onCommit={async (desc) => {
                  const cityName = (desc || '').split(',')[0].trim();
                  if (cityName.length < 3) return;
                  try {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                    const token = localStorage.getItem('ds_token');
                    const r = await fetch(`${API_URL}/travel/airports?q=${encodeURIComponent(cityName)}`, {
                      headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
                    });
                    const data = await r.json();
                    const top = (data || [])[0];
                    if (top) {
                      setArrivalAirport(top.iata || top.icao);
                      if (top.tz) setArrivalTz(top.tz);
                    }
                  } catch { /* non-fatal */ }
                }}
                placeholder="Las Vegas, NV" /></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>From (Airport)</SLabel><AirportSearch value={departureAirport} onChange={(code, airport) => {
                setDepartureAirport(code);
                // Reverse cascade: picking an airport directly still
                // backfills its City — useful when admin knows the code
                // (EWR) but didn't think to start with the city.
                if (airport?.city) {
                  const tail = airport.country && airport.country !== 'US'
                    ? `, ${airport.country}`
                    : (airport.state ? `, ${airport.state}` : '');
                  setDepartureCity(`${airport.city}${tail}`);
                }
                if (airport?.tz) setDepartureTz(airport.tz);
              }} placeholder={flightType==='commercial'?"LAX":"KOPF"} /></div>
              <div><SLabel>To (Airport)</SLabel><AirportSearch value={arrivalAirport} onChange={(code, airport) => {
                setArrivalAirport(code);
                if (airport?.city) {
                  const tail = airport.country && airport.country !== 'US'
                    ? `, ${airport.country}`
                    : (airport.state ? `, ${airport.state}` : '');
                  setArrivalCity(`${airport.city}${tail}`);
                }
                if (airport?.tz) setArrivalTz(airport.tz);
              }} placeholder={flightType==='commercial'?"JFK":"KMMU"} /></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <SLabel>
                  Departure Time
                  {departureTz && <span style={{ marginLeft:6, fontSize:10, fontWeight:700, color:'#06b6d4', background:'rgba(6,182,212,0.1)', border:'1px solid rgba(6,182,212,0.25)', borderRadius:4, padding:'2px 6px', letterSpacing:0.5 }}>{departureTz}</span>}
                </SLabel>
                <TimePicker value={flightDepartureTime} onChange={setFlightDepartureTime} placeholder="Departure" />
                {departureTz && <div style={{ fontSize:10, color:'var(--text3)', marginTop:4, lineHeight:1.4 }}>Local at {departureAirport || 'departure airport'} — what your watch reads at the gate.</div>}
              </div>
              <div>
                <SLabel>
                  Arrival Time
                  {arrivalTz && <span style={{ marginLeft:6, fontSize:10, fontWeight:700, color:'#06b6d4', background:'rgba(6,182,212,0.1)', border:'1px solid rgba(6,182,212,0.25)', borderRadius:4, padding:'2px 6px', letterSpacing:0.5 }}>{arrivalTz}</span>}
                </SLabel>
                <TimePicker value={flightArrivalTime} onChange={setFlightArrivalTime} placeholder="Arrival" />
                {arrivalTz && <div style={{ fontSize:10, color:'var(--text3)', marginTop:4, lineHeight:1.4 }}>Local at {arrivalAirport || 'arrival airport'} — what your watch reads on landing.</div>}
              </div>
            </div>
            {departureTz && arrivalTz && departureTz !== arrivalTz && (
              <div style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#fbbf24', display:'flex', alignItems:'flex-start', gap:8 }}>
                <i className="fa-solid fa-circle-info" style={{ marginTop:2 }}></i>
                <div>
                  <strong>Cross-zone flight ({departureTz} → {arrivalTz}).</strong> Enter each time in <em>that airport's</em> local zone — match the boarding pass. The sheet will auto-convert when displaying.
                </div>
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Flight Duration</SLabel><input value={flightDuration} onChange={e=>setFlightDuration(e.target.value)} placeholder="10hr 45min" style={inp} /></div>
              <div><SLabel>Cabin Class</SLabel><input value={cabinClass} onChange={e=>setCabinClass(e.target.value)} placeholder="First, Business, Economy" style={inp} /></div>
            </div>

            {flightType === 'private' && <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><SLabel>Departure FBO</SLabel><input value={departureFBO} onChange={e=>setDepartureFBO(e.target.value)} placeholder="Signature Aviation" style={inp} /></div>
                <div><SLabel>FBO Phone</SLabel><input value={departureFBOPhone} onChange={e=>setDepartureFBOPhone(e.target.value)} placeholder="(305) 687-8410" style={inp} /></div>
              </div>
              <div><SLabel>Departure FBO Address</SLabel><LocationSearch value={departureFBOAddress} onChange={setDepartureFBOAddress} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><SLabel>Arrival FBO</SLabel><input value={arrivalFBO} onChange={e=>setArrivalFBO(e.target.value)} placeholder="Signature Aviation" style={inp} /></div>
                <div><SLabel>FBO Phone</SLabel><input value={arrivalFBOPhone} onChange={e=>setArrivalFBOPhone(e.target.value)} placeholder="(973) 292-1300" style={inp} /></div>
              </div>
              <div><SLabel>Arrival FBO Address</SLabel><LocationSearch value={arrivalFBOAddress} onChange={setArrivalFBOAddress} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><SLabel>Requested By</SLabel><input value={requestedBy} onChange={e=>setRequestedBy(e.target.value)} placeholder="Jeremy Book" style={inp} /></div>
                <div><SLabel>Phone</SLabel><input value={requestedByPhone} onChange={e=>setRequestedByPhone(e.target.value)} placeholder="(973) 356-9783" style={inp} /></div>
              </div>
            </>}

            {flightType === 'commercial' && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><SLabel>Seat</SLabel><input value={seat} onChange={e=>setSeat(e.target.value)} placeholder="12A" style={inp} /></div>
                <div><SLabel>Confirmation</SLabel><input value={flightConfirmation} onChange={e=>setFlightConfirmation(e.target.value)} style={inp} /></div>
              </div>
            )}
            {flightType === 'private' && (
              <div><SLabel>Confirmation / Reservation #</SLabel><input value={flightConfirmation} onChange={e=>setFlightConfirmation(e.target.value)} placeholder="16561405" style={inp} /></div>
            )}
            {/* Departure logistics */}
            <p style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:1, textTransform:'uppercase', margin:'12px 0 4px' }}>Departure</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Greeter Name</SLabel><input value={departureGreeterName} onChange={e=>setDepartureGreeterName(e.target.value)} placeholder="Holly" style={inp} /></div>
              <div><SLabel>Greeter Phone</SLabel><input value={departureGreeterPhone} onChange={e=>setDepartureGreeterPhone(e.target.value)} placeholder="555-123-4567" style={inp} /></div>
            </div>
            {departureDrivers.map((d,i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:12, alignItems:'end' }}>
                <div><SLabel>Driver {departureDrivers.length > 1 ? i+1 : ''} Name</SLabel><input value={d.name} onChange={e=>{ const u=[...departureDrivers]; u[i]={...d,name:e.target.value}; setDepartureDrivers(u); }} style={inp} /></div>
                <div><SLabel>Phone</SLabel><input value={d.phone||''} onChange={e=>{ const u=[...departureDrivers]; u[i]={...d,phone:e.target.value}; setDepartureDrivers(u); }} style={inp} /></div>
                <button onClick={()=>setDepartureDrivers(prev=>prev.filter((_,j)=>j!==i))} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:14, padding:'10px 4px', marginBottom:1 }}>×</button>
              </div>
            ))}
            <button onClick={()=>setDepartureDrivers(prev=>[...prev,{name:'',phone:''}])} style={{ fontSize:12, fontWeight:600, color:'var(--blue)', background:'none', border:'none', cursor:'pointer', padding:'4px 0', fontFamily:'inherit' }}>
              <i className="fa-solid fa-plus" style={{ fontSize:10, marginRight:4 }}></i>Add departure driver
            </button>

            {/* Arrival logistics */}
            <p style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:1, textTransform:'uppercase', margin:'12px 0 4px' }}>Arrival</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Greeter Name</SLabel><input value={greeterName} onChange={e=>setGreeterName(e.target.value)} placeholder="Holly" style={inp} /></div>
              <div><SLabel>Greeter Phone</SLabel><input value={greeterPhone} onChange={e=>setGreeterPhone(e.target.value)} placeholder="555-123-4567" style={inp} /></div>
            </div>
            {arrivalDrivers.map((d,i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:12, alignItems:'end' }}>
                <div><SLabel>Driver {arrivalDrivers.length > 1 ? i+1 : ''} Name</SLabel><input value={d.name} onChange={e=>{ const u=[...arrivalDrivers]; u[i]={...d,name:e.target.value}; setArrivalDrivers(u); }} style={inp} /></div>
                <div><SLabel>Phone</SLabel><input value={d.phone||''} onChange={e=>{ const u=[...arrivalDrivers]; u[i]={...d,phone:e.target.value}; setArrivalDrivers(u); }} style={inp} /></div>
                <button onClick={()=>setArrivalDrivers(prev=>prev.filter((_,j)=>j!==i))} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:14, padding:'10px 4px', marginBottom:1 }}>×</button>
              </div>
            ))}
            <button onClick={()=>setArrivalDrivers(prev=>[...prev,{name:'',phone:''}])} style={{ fontSize:12, fontWeight:600, color:'var(--blue)', background:'none', border:'none', cursor:'pointer', padding:'4px 0', fontFamily:'inherit' }}>
              <i className="fa-solid fa-plus" style={{ fontSize:10, marginRight:4 }}></i>Add arrival driver
            </button>

            {!isEdit && (
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'8px 0' }}>
                <input type="checkbox" checked={createEvents} onChange={e=>setCreateEvents(e.target.checked)}
                  style={{ width:16, height:16, accentColor:'var(--blue)' }} />
                <span style={{ fontSize:13, fontWeight:600, color:'var(--text2)' }}>
                  <i className="fa-regular fa-calendar-plus" style={{ marginRight:4, fontSize:11 }}></i>
                  Auto-create departure &amp; arrival events
                </span>
              </label>
            )}
          </>}
          {type==='carservice' && <>
            <div><SLabel>Company</SLabel><input value={carServiceCompany} onChange={e=>setCarServiceCompany(e.target.value)} placeholder="Blacklane, Carey..." style={inp} /></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Driver Name</SLabel><input value={carServiceDriver} onChange={e=>setCarServiceDriver(e.target.value)} style={inp} /></div>
              <div><SLabel>Driver Phone</SLabel><input value={carServicePhone} onChange={e=>setCarServicePhone(e.target.value)} style={inp} /></div>
            </div>
            <div><SLabel>Pickup Location</SLabel><LocationSearch value={carServicePickup} onChange={setCarServicePickup} /></div>
            <div><SLabel>Dropoff Location</SLabel><LocationSearch value={carServiceDropoff} onChange={setCarServiceDropoff} /></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Pickup Time</SLabel><TimePicker value={carServiceTime} onChange={setCarServiceTime} placeholder="Pickup time" /></div>
              <div><SLabel>Confirmation</SLabel><input value={carServiceConfirmation} onChange={e=>setCarServiceConfirmation(e.target.value)} style={inp} /></div>
            </div>
          </>}
          {type==='train' && <>
            <div><SLabel>Train Company</SLabel><input value={trainCompany} onChange={e=>setTrainCompany(e.target.value)} placeholder="Amtrak, Eurostar..." style={inp} /></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>From</SLabel><input value={trainFrom} onChange={e=>setTrainFrom(e.target.value)} style={inp} /></div>
              <div><SLabel>To</SLabel><input value={trainTo} onChange={e=>setTrainTo(e.target.value)} style={inp} /></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><SLabel>Departure</SLabel><TimePicker value={departureTime} onChange={setDepartureTime} placeholder="Departure" /></div>
              <div><SLabel>Arrival</SLabel><TimePicker value={arrivalTime} onChange={setArrivalTime} placeholder="Arrival" /></div>
            </div>
            <div><SLabel>Confirmation</SLabel><input value={trainConfirmation} onChange={e=>setTrainConfirmation(e.target.value)} style={inp} /></div>
          </>}
          <div><SLabel>Additional Notes</SLabel><textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2} placeholder="Add any additional notes..." style={{ ...inp, resize:'vertical', fontFamily:'inherit' }} /></div>
          {error && <p style={{ fontSize:13, color:'var(--red)' }}>{error}</p>}
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => handleSave(false)} disabled={saving}
              style={{ flex:1, padding:'14px', borderRadius:12, border:'none',
                background:'linear-gradient(135deg, #1E40AF, #6366f1)', color:'#fff',
                cursor: saving ? 'wait' : 'pointer', fontFamily:'inherit', fontSize:15, fontWeight:800,
                opacity: saving ? 0.6 : 1, transition:'all 0.15s' }}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Travel'}
            </button>
            <button onClick={() => handleSave(true)} disabled={saving}
              style={{ flex:1, padding:'14px', borderRadius:12,
                background:'rgba(34,197,94,0.08)', border:'1.5px solid rgba(34,197,94,0.3)', color:'#22c55e',
                cursor: saving ? 'wait' : 'pointer', fontFamily:'inherit', fontSize:14, fontWeight:800,
                opacity: saving ? 0.6 : 1, transition:'all 0.15s', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-brands fa-google" style={{ fontSize:11 }}></i>
              {isEdit ? 'Save + GCal' : 'Add + GCal'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TravelCard({ record, onEdit, onDelete }) {
  const t = TRAVEL_TYPES.find(t => t.id===record.type);
  const dateStr = record.startDate===record.endDate ? record.startDate : record.startDate+' to '+record.endDate;
  const title = record.type==='flight'?(record.airline?record.airline+' ':'')+record.flightNumber:record.type==='hotel'?record.hotelName:record.type==='carservice'?record.carServiceCompany||'Car Service':record.type==='car'?record.carCompany:record.type==='ground'?record.driverName:record.trainCompany;
  const subtitle = record.type==='flight'?(record.departureAirport||'?')+' → '+(record.arrivalAirport||'?')+(record.passenger?' · '+record.passenger:''):record.type==='hotel'?record.hotelAddress:record.type==='carservice'?(record.carServiceDriver||record.carServicePickup||''):record.type==='car'?('Pickup: '+record.carPickupLocation):record.type==='ground'?record.driverPhone:(record.trainFrom+' to '+record.trainTo);
  const confirmation = record.hotelConfirmation||record.carConfirmation||record.trainConfirmation;
  return (
    <Card style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
      <div style={{ width:44, height:44, borderRadius:12, background:'rgba(79,156,249,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}><i className={`fa-solid ${t?.icon}`}></i></div>
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', marginBottom:2 }}>{title||t?.label}</p>
        {subtitle && <p style={{ fontSize:13, color:'var(--text2)', marginBottom:2 }}>{subtitle}</p>}
        <p style={{ fontSize:12, color:'var(--text3)' }}><i className="fa-regular fa-calendar" style={{ marginRight:4 }}></i>{dateStr}{confirmation?' · # '+confirmation:''}</p>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={() => onEdit(record)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:16 }}><i className="fa-solid fa-pen"></i></button>
        <button onClick={() => onDelete(record.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:16 }}>✕</button>
      </div>
    </Card>
  );
}

export default function TravelPage() {
  const { colorMap } = usePeople();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [artists,setArtists] = useState([]);
  const [selArtist,setSelArtist] = useState(null);
  const [records,setRecords] = useState([]);
  const [loading,setLoading] = useState(false);
  const [modal,setModal] = useState(null);
  const [filter,setFilter] = useState('all');
  const fileRef = useRef();
  const [extracting,setExtracting] = useState(false);
  const [extractError,setExtractError] = useState('');
  const [extractedTravel, setExtractedTravel] = useState([]);
  const [extractedIdx, setExtractedIdx] = useState(0);
  const [gcalTravel, setGcalTravel] = useState([]);
  const [gcalLoading, setGcalLoading] = useState(false);
  const [gcalImporting, setGcalImporting] = useState(null);

  useEffect(() => {
    api.getArtists().then(d => {
      const list = d.artists||[];
      setArtists(list);
      if (list.length) setSelArtist(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selArtist) return;
    setLoading(true);
    api.getAllTravel(selArtist).then(setRecords).catch(()=>setRecords([])).finally(()=>setLoading(false));
  }, [selArtist]);

  const handleSave = (result) => {
    setRecords(prev => { const i=prev.findIndex(r=>r.id===result.id); if(i>=0){const n=[...prev];n[i]=result;return n;} return [...prev,result]; });
    setModal(null);
  };

  const handleDelete = async (id) => {
    if (!await confirmAction('Delete this travel record?', { title: 'Delete Travel', confirmLabel: 'Delete' })) return;
    await api.deleteTravel(id);
    setRecords(prev => prev.filter(r => r.id!==id));
  };

  const handleDuplicate = async (record, targetDate) => {
    // Clone the record with the new date
    const { id, createdAt, updatedAt, ...body } = record;
    body.startDate = targetDate;
    body.endDate = targetDate;
    try {
      const result = await api.createTravel(body);
      setRecords(prev => [...prev, result]);
    } catch (e) { console.error('Duplicate failed:', e); }
  };

  // Load GCal travel events when date changes
  const loadGcalTravel = async (artistId, date) => {
    if (!artistId || !date) return;
    setGcalLoading(true);
    try {
      const data = await api.gcalTravel(artistId, date);
      setGcalTravel(data.parsed || []);
    } catch { setGcalTravel([]); }
    finally { setGcalLoading(false); }
  };

  const handleImportGcalTravel = async (parsed) => {
    if (gcalImporting) return;
    const evId = parsed.gcalEventId || Date.now();
    setGcalImporting(evId);
    const body = { artistId: selArtist, type: parsed.type || 'flight',
      startDate: parsed.startDate || selDate, endDate: parsed.endDate || selDate,
      airline: parsed.airline || null, flightNumber: parsed.flightNumber || null,
      departureAirport: parsed.departureAirport || null, arrivalAirport: parsed.arrivalAirport || null,
      flightDepartureTime: parsed.flightDepartureTime || null, flightArrivalTime: parsed.flightArrivalTime || null,
      seat: parsed.seat || null, flightConfirmation: parsed.flightConfirmation || null,
      passenger: parsed.passenger || null, flightType: parsed.flightType || 'commercial',
      tailNumber: parsed.tailNumber || null, aircraftType: parsed.aircraftType || null, operator: parsed.operator || null,
      hotelName: parsed.hotelName || null, hotelAddress: parsed.hotelAddress || null, hotelConfirmation: parsed.hotelConfirmation || null, roomType: parsed.roomType || null,
      carServiceCompany: parsed.carServiceCompany || null, carServiceDriver: parsed.carServiceDriver || null,
      carServicePhone: parsed.carServicePhone || null, carServicePickup: parsed.carServicePickup || null,
      carServiceDropoff: parsed.carServiceDropoff || null, carServiceTime: parsed.carServiceTime || null,
      driverName: parsed.driverName || null, driverPhone: parsed.driverPhone || null,
      pickupLocation: parsed.pickupLocation || null, dropoffLocation: parsed.dropoffLocation || null,
      notes: parsed.notes || null };
    try {
      const result = await api.createTravel(body);
      if (result && result.id) {
        setRecords(prev => [...prev, result]);
        setGcalTravel(prev => prev.map(p => (p.gcalEventId || p) === (parsed.gcalEventId || parsed) ? { ...p, alreadyImported: true } : p));
      }
    } catch (e) {
      console.error('GCal travel import error:', e);
    } finally {
      setGcalImporting(null);
    }
  };

  const handleExtract = async (files) => {
    const supported = Array.from(files).filter(f =>
      f.type==='application/pdf' || f.name.endsWith('.pdf') ||
      f.type==='text/plain' || f.name.endsWith('.eml') || f.name.endsWith('.txt') ||
      f.type.startsWith('image/')
    );
    if (!supported.length) { setExtractError('Drop PDFs, images, or email files'); return; }
    setExtracting(true); setExtractError('');
    try {
      for (const file of supported) {
        const reader = new FileReader();
        const base64 = await new Promise(r => { reader.onload=()=>r(reader.result.split(',')[1]); reader.readAsDataURL(file); });
        const mimeType = file.type.startsWith('image/') ? file.type : file.name.endsWith('.eml') ? 'text/plain' : file.type || 'application/pdf';
        const docType = file.type.startsWith('image/') ? 'image' : 'document';
        const res = await apiFetch('/sheets/extract-events', { method:'POST', body: JSON.stringify({
          base64, mimeType,
          prompt: `Extract ALL travel reservations from this ${docType}. This could be a flight confirmation, hotel booking, car rental, train ticket, ground transport details, or a forwarded email/screenshot of any of these. Return ONLY a valid JSON array with no extra text. Do NOT use newlines inside string values.

Each item must have:
- type: "flight", "hotel", "car", "ground", or "train"
- startDate: YYYY-MM-DD format
- endDate: YYYY-MM-DD format

For FLIGHTS include:
- flightType: "commercial" or "private" (use "private" for NetJets, Privé Jets, charter flights, Part 91K/135 flights, any flight with tail numbers like N154EA)
- airline: airline name or charter company (United, Delta, NetJets, Privé Jets, etc.)
- flightNumber: flight number or reservation/request number
- departureAirport: 3 or 4-letter airport code (LAX, JFK, KOPF, KTEB, etc.)
- arrivalAirport: 3 or 4-letter airport code
- departureCity: city and state/country (e.g. "New York City, NY", "Buenos Aires, AR")
- arrivalCity: city and state/country
- flightDepartureTime: HH:MM in 24hr local departure time
- flightArrivalTime: HH:MM in 24hr local arrival time
- flightDuration: flight duration (e.g. "10hr 45min", "2hr 30min")
- cabinClass: cabin class if shown (e.g. "First", "Business", "Economy")
- seat: seat assignment if visible (commercial only)
- flightConfirmation: confirmation/record locator/reservation number
- passenger: comma-separated list of ALL passenger names in NATURAL order "First Last" — convert airline format LAST/FIRST to "First Last" (e.g. "WEIR/GEORGE" → "George Weir", "JONAS II/PAUL KEVIN" → "Kevin Jonas II")
For PRIVATE AVIATION also include:
- tailNumber: aircraft tail/registration number (e.g. "N154EA")
- aircraftType: aircraft model (e.g. "Citation Latitude", "Challenger 850")
- operator: operating company name (e.g. "NetJets", "Privé Jets")
- departureFBO: departure FBO name (e.g. "Signature Aviation")
- departureFBOPhone: FBO phone number
- departureFBOAddress: FBO street address
- arrivalFBO: arrival FBO name
- arrivalFBOPhone: arrival FBO phone
- arrivalFBOAddress: arrival FBO address
- requestedBy: contact person name
- requestedByPhone: contact person phone

For HOTELS include:
- hotelName: full hotel name
- hotelAddress: full street address
- hotelConfirmation: confirmation number
- roomType: room type if available
Look for: RESERVATION CODE, Confirmation Number, Check-In/Check-Out dates
Dates may appear as "16 MAR 2026", "03/06/2026", "Friday 05/22" — convert all to YYYY-MM-DD

For CARS include: carCompany, carPickupLocation, carDropoffLocation, carConfirmation
For TRAINS include: trainCompany, trainFrom, trainTo, trainConfirmation, departureTime (HH:MM), arrivalTime (HH:MM)
For GROUND transport: driverName, driverPhone, pickupLocation, dropoffLocation

Do NOT include a notes field — leave notes null.
Return [] if no travel info found. Never include disclaimers in the JSON.`
        })});
        let clean = (res.text||'').replace(/```json|```/g,'').trim();
        const arrMatch = clean.match(/\[[\s\S]*\]/);
        if (!arrMatch) { setExtractError('No travel data found'); continue; }
        clean = arrMatch[0].replace(/,\s*]/g, ']').replace(/,\s*}/g, '}');
        const extracted = JSON.parse(clean);
        if (!extracted.length) { setExtractError('No travel records found'); continue; }
        // Merge duplicate flights — same flight + airports → combine passengers
        const merged = [];
        const seen = {};
        for (const t of extracted) {
          if (t.type === 'flight' && t.departureAirport && t.arrivalAirport) {
            const key = t.flightNumber
              ? `${t.flightNumber}-${t.departureAirport}-${t.arrivalAirport}`
              : `${t.departureAirport}-${t.arrivalAirport}-${t.flightDepartureTime||''}-${t.startDate||''}`;
            if (seen[key] !== undefined) {
              const ex = merged[seen[key]];
              const ep = (ex.passenger||'').split(',').map(p=>p.trim()).filter(Boolean);
              const np = (t.passenger||'').split(',').map(p=>p.trim()).filter(Boolean);
              ex.passenger = [...new Set([...ep, ...np])].join(', ');
              if (t.seat && ex.seat && !ex.seat.includes(t.seat)) ex.seat = `${ex.seat}, ${t.seat}`;
              else if (t.seat && !ex.seat) ex.seat = t.seat;
              if (t.flightConfirmation && ex.flightConfirmation && !ex.flightConfirmation.includes(t.flightConfirmation)) ex.flightConfirmation = `${ex.flightConfirmation}, ${t.flightConfirmation}`;
              else if (t.flightConfirmation && !ex.flightConfirmation) ex.flightConfirmation = t.flightConfirmation;
              ['departureCity','arrivalCity','flightDuration','cabinClass','airline','flightNumber','tailNumber','aircraftType','operator'].forEach(f => { if (t[f] && !ex[f]) ex[f] = t[f]; });
            } else { seen[key] = merged.length; merged.push({ ...t }); }
          } else { merged.push(t); }
        }
        // Open first record in TravelFormModal for review; queue rest
        setExtractedTravel(merged);
        setExtractedIdx(0);
      }
    } catch(e) { setExtractError('Extraction failed: '+e.message); }
    finally { setExtracting(false); }
  };

  const today = new Date().toISOString().slice(0,10);
  const filtered = filter==='all' ? records : records.filter(r=>r.type===filter);
  // When a specific date is selected, show travel active on that date; otherwise show upcoming/past
  const upcoming = filtered.filter(r => r.endDate >= today || r.startDate >= today).sort((a,b) => a.startDate.localeCompare(b.startDate));
  const past = filtered.filter(r => r.endDate < today && r.startDate < today).sort((a,b) => b.startDate.localeCompare(a.startDate));
  const [showPast, setShowPast] = useState(false);
  const [openPastDates, setOpenPastDates] = useState(new Set());
  const [viewRecord, setViewRecord] = useState(null);
  const [tab, setTab] = useState('date');
  const [selDate, setSelDate] = useState(today);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [showCal, setShowCal] = useState(false);
  const activeArtist = artists.find(a => a.id === selArtist);

  // Load GCal travel when date or artist changes (skip during import)
  useEffect(() => {
    if (!gcalImporting) loadGcalTravel(selArtist, selDate);
  }, [selArtist, selDate]); // eslint-disable-line

  // When a date is picked, filter to that date; otherwise show all upcoming
  const dateFiltered = selDate !== today
    ? filtered.filter(r => r.startDate <= selDate && r.endDate >= selDate)
    : upcoming;

  // Group by date for timeline display
  const upcomingByDate = {};
  dateFiltered.forEach(r => {
    const d = r.startDate || 'Unknown';
    if (!upcomingByDate[d]) upcomingByDate[d] = [];
    upcomingByDate[d].push(r);
  });
  const upcomingDateKeys = Object.keys(upcomingByDate).sort((a,b) => a.localeCompare(b));

  return (
    <div style={{ padding: window.innerWidth < 768 ? '20px 16px' : '32px 40px', maxWidth:900, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:32, fontWeight:900, letterSpacing:-0.5, color:'var(--text)' }}>Travel</h1>
        <p style={{ fontSize:14, color:'var(--text2)', marginTop:4 }}>Flights, hotels, and ground transport</p>
      </div>
      {/* Artist picker — circle avatars with names */}
      {artists.length > 1 && (
        <div style={{ display:'flex', gap:16, marginBottom:20, flexWrap:'wrap', justifyContent:'center' }}>
          {artists.map(a => {
            const isSel = selArtist===a.id;
            return (
              <button key={a.id} onClick={() => setSelArtist(a.id)}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:0, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', opacity: isSel ? 1 : 0.6, transition:'all 0.15s', minWidth:68 }}>
                <div style={{ width:60, height:60, borderRadius:'50%', border:`2.5px solid ${isSel ? 'var(--blue)' : 'var(--border)'}`,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, overflow:'hidden',
                  background: a.photo ? 'none' : `${a.color||'var(--card)'}20`,
                  boxShadow: isSel ? '0 0 0 3px rgba(79,156,249,0.25)' : 'none', transition:'all 0.15s' }}>
                  {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji||a.name[0])}
                </div>
                <span style={{ fontSize:11, fontWeight:700, color: isSel ? 'var(--blue)' : 'var(--text2)', maxWidth:68, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Date picker */}
      <div style={{ marginBottom:16 }}>
        <CalendarPicker value={selDate} onChange={setSelDate} />
      </div>

      {/* Action buttons — matching SheetBuilder/Events pattern */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
        <button onClick={() => setModal('new')}
          style={{ padding:'12px 16px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'1.5px solid transparent', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 12px rgba(30,64,175,0.25)' }}>
          <i className="fa-solid fa-plus" style={{ fontSize:11 }}></i> Add Travel
        </button>
        <div style={{ position:'relative', display:'flex' }}>
          <button
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor='rgba(168,85,247,0.6)'; e.currentTarget.style.background='rgba(168,85,247,0.08)'; }}
            onDragLeave={e => { e.currentTarget.style.borderColor='rgba(168,85,247,0.4)'; e.currentTarget.style.background='transparent'; }}
            onDrop={e=>{e.preventDefault(); e.currentTarget.style.borderColor='rgba(168,85,247,0.4)'; e.currentTarget.style.background='transparent'; handleExtract(e.dataTransfer.files);}}
            onClick={()=>fileRef.current?.click()}
            style={{ flex:1, padding:'12px 16px', borderRadius:12, background:'transparent', border:'1.5px dashed rgba(168,85,247,0.4)', color:'#a855f7', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxSizing:'border-box' }}>
            <input ref={fileRef} type="file" multiple accept=".pdf,.eml,.txt,.jpg,.jpeg,.png,.heic,.webp" style={{ display:'none' }} onChange={e=>handleExtract(e.target.files)} />
            {extracting
              ? <><i className="fa-solid fa-spinner fa-spin" style={{ fontSize:11 }}></i> Importing...</>
              : <><i className="fa-solid fa-file-import" style={{ fontSize:11 }}></i> Import Itinerary</>
            }
          </button>
        </div>
      </div>
      {extractError && <p style={{ color:'var(--red)', fontSize:12, marginTop:-10, marginBottom:12 }}>{extractError}</p>}

      {/* ── Extracted travel → opens TravelFormModal pre-filled ── */}
      {extractedTravel.length > 0 && extractedIdx < extractedTravel.length && (
        <TravelFormModal
          record={extractedTravel[extractedIdx]}
          artistId={selArtist}
          artists={artists}
          onSave={(result) => {
            setRecords(prev => [...prev, result]);
            const nextIdx = extractedIdx + 1;
            if (nextIdx < extractedTravel.length) {
              setExtractedIdx(nextIdx);
            } else {
              setExtractedTravel([]);
              setExtractedIdx(0);
            }
          }}
          onClose={() => { setExtractedTravel([]); setExtractedIdx(0); }}
        />
      )}

      {/* Source toggle buttons — matching Events page */}
      {(() => {
        const onDate = filtered.filter(r => r.startDate <= selDate && r.endDate >= selDate);
        return (<>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom: tab ? 10 : 16 }}>
            <button onClick={() => setTab(tab==='date' ? '' : 'date')}
              style={{ padding:'12px 16px', borderRadius:12, background: tab==='date' ? 'rgba(30,64,175,0.12)' : 'var(--card)', border: tab==='date' ? '1.5px solid rgba(30,64,175,0.4)' : '1.5px solid var(--border)', color: tab==='date' ? '#1E40AF' : 'var(--text2)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, transition:'all 0.15s' }}>
              <i className="fa-solid fa-calendar-day" style={{ fontSize:11 }}></i> On Date
              {onDate.length > 0 && <span style={{ fontSize:10, fontWeight:700, color:'#1E40AF', background:'rgba(30,64,175,0.15)', padding:'2px 7px', borderRadius:8 }}>{onDate.length}</span>}
              <i className={`fa-solid fa-chevron-${tab==='date' ? 'up' : 'down'}`} style={{ fontSize:9, marginLeft:'auto', opacity:0.6 }}></i>
            </button>
            <button onClick={() => setTab(tab==='gcal' ? '' : 'gcal')}
              style={{ padding:'12px 16px', borderRadius:12, background: tab==='gcal' ? 'rgba(30,64,175,0.12)' : 'var(--card)', border: tab==='gcal' ? '1.5px solid rgba(30,64,175,0.4)' : '1.5px solid var(--border)', color: tab==='gcal' ? '#1E40AF' : 'var(--text2)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, transition:'all 0.15s' }}>
              <i className="fab fa-google" style={{ fontSize:11 }}></i> GCal
              {gcalTravel.length > 0 && <span style={{ fontSize:10, fontWeight:700, color:'#1E40AF', background:'rgba(30,64,175,0.15)', padding:'2px 7px', borderRadius:8 }}>{gcalTravel.length}</span>}
              <i className={`fa-solid fa-chevron-${tab==='gcal' ? 'up' : 'down'}`} style={{ fontSize:9, marginLeft:'auto', opacity:0.6 }}></i>
            </button>
            <button onClick={() => setTab(tab==='all' ? '' : 'all')}
              style={{ padding:'12px 16px', borderRadius:12, background: tab==='all' ? 'rgba(30,64,175,0.12)' : 'var(--card)', border: tab==='all' ? '1.5px solid rgba(30,64,175,0.4)' : '1.5px solid var(--border)', color: tab==='all' ? '#1E40AF' : 'var(--text2)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, transition:'all 0.15s' }}>
              <i className="fa-solid fa-list" style={{ fontSize:11 }}></i> All
              <span style={{ fontSize:10, fontWeight:700, color:'#1E40AF', background:'rgba(30,64,175,0.15)', padding:'2px 7px', borderRadius:8 }}>{filtered.length}</span>
              <i className={`fa-solid fa-chevron-${tab==='all' ? 'up' : 'down'}`} style={{ fontSize:9, marginLeft:'auto', opacity:0.6 }}></i>
            </button>
          </div>

          {/* On Date panel */}
          {tab === 'date' && (
            onDate.length > 0 ? (
              <Card style={{ padding:0, marginBottom:16, borderTop:'2px solid rgba(30,64,175,0.4)' }}>
                <div style={{ padding:'10px 8px 10px 4px' }}>
                  <TravelTimeline records={onDate} onSelect={r => setViewRecord(r)} onEdit={r => setModal(r)} onDelete={handleDelete} colorMap={colorMap} />
                </div>
              </Card>
            ) : (
              <Card style={{ padding:'24px 16px', marginBottom:16, borderTop:'2px solid rgba(30,64,175,0.4)', textAlign:'center' }}>
                <p style={{ fontSize:13, color:'var(--text3)', margin:0 }}>No travel for this date</p>
              </Card>
            )
          )}
        </>);
      })()}

      {/* Google Calendar Travel — toggle panel */}
      {tab === 'gcal' && <Card style={{ padding:0, marginBottom:16, overflow:'hidden', borderTop:'2px solid rgba(30,64,175,0.4)' }}>
        <div style={{ padding:'14px 16px 10px', borderBottom: gcalTravel.length > 0 ? '1px solid var(--border)' : 'none', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>
            <i className="fa-brands fa-google" style={{ marginRight:6, fontSize:10 }}></i>CALENDAR TRAVEL
          </p>
          {gcalLoading ? (
            <span style={{ fontSize:11, color:'var(--text3)' }}><i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize:9, marginRight:4 }}></i>Scanning...</span>
          ) : gcalTravel.length > 0 ? (
            <span style={{ fontSize:11, color:'var(--blue)', fontWeight:600 }}>{gcalTravel.length} detected</span>
          ) : (
            <span style={{ fontSize:11, color:'var(--text3)' }}>No travel found</span>
          )}
          </div>
          {gcalTravel.length > 0 && <div>
            {gcalTravel.map((p, i) => {
              const tColors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
              const tIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
              const c = tColors[p.type] || '#1E40AF';
              const title = p.type === 'flight' ? `${p.airline||''} ${p.flightNumber||''}`.trim() || 'Flight'
                : p.type === 'hotel' ? p.hotelName || 'Hotel'
                : p.type === 'carservice' ? p.carServiceCompany || 'Car Service'
                : p.type === 'ground' ? p.driverName || 'Ground' : 'Travel';
              const route = p.type === 'flight' ? `${p.departureAirport||'?'} → ${p.arrivalAirport||'?'}` : '';
              const fmt12 = (t) => { if (!t) return ''; const [h,m]=t.split(':').map(Number); if(isNaN(h)) return t; return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; };
              return (
                <div key={p.gcalEventId||i} style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px',
                  borderBottom: i < gcalTravel.length-1 ? '1px solid var(--border)' : 'none',
                  opacity: p.alreadyImported ? 0.5 : 1 }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:`${c}14`, border:`1.5px solid ${c}33`,
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <i className={`fa-solid ${tIcons[p.type]||'fa-circle'}`} style={{ fontSize:14, color:c }}></i>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>
                      {title}{route ? <span style={{ color:c, marginLeft:6 }}>{route}</span> : ''}
                    </p>
                    <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0' }}>
                      {p.flightDepartureTime ? fmt12(p.flightDepartureTime) : ''}{p.flightDepartureTime && p.flightArrivalTime ? ' → ' : ''}{p.flightArrivalTime ? fmt12(p.flightArrivalTime) : ''}
                      {p.passenger ? (p.flightDepartureTime ? ' · ' : '') + p.passenger : ''}
                      {p.seat ? ' · Seat ' + p.seat : ''}
                      {p.flightConfirmation ? ' · ' + p.flightConfirmation : ''}
                    </p>
                  </div>
                  {p.alreadyImported ? (
                    <button onClick={async () => {
                      // Find and delete the matching travel record
                      const match = records.find(t => {
                        if (p.type === 'flight' && t.type === 'flight')
                          return t.departureAirport === p.departureAirport && t.arrivalAirport === p.arrivalAirport && t.startDate === p.startDate;
                        if (p.type === 'hotel' && t.type === 'hotel')
                          return t.hotelName?.toLowerCase() === p.hotelName?.toLowerCase();
                        return false;
                      });
                      if (match) {
                        await api.deleteTravel(match.id);
                        setRecords(prev => prev.filter(r => r.id !== match.id));
                        setGcalTravel(prev => prev.map(g => g.gcalEventId === p.gcalEventId ? { ...g, alreadyImported: false } : g));
                      }
                    }}
                      style={{ fontSize:11, fontWeight:700, color:'#3fb950', background:'none', border:'none', cursor:'pointer', flexShrink:0, fontFamily:'inherit' }}
                      title="Click to remove">
                      Imported ×
                    </button>
                  ) : (
                    <button onClick={() => handleImportGcalTravel(p)}
                      disabled={gcalImporting && gcalImporting === p.gcalEventId}
                      style={{ padding:'8px 14px', borderRadius:10, border:'none', cursor: (gcalImporting && gcalImporting===p.gcalEventId) ? 'wait' : 'pointer',
                        background: (gcalImporting && gcalImporting===p.gcalEventId) ? 'var(--text3)' : 'var(--blue)',
                        color:'#fff', fontFamily:'inherit', fontSize:12, fontWeight:700, flexShrink:0, display:'flex', alignItems:'center', gap:5 }}>
                      <i className="fa-solid fa-plus" style={{ fontSize:10 }}></i>
                      {(gcalImporting && gcalImporting === p.gcalEventId) ? 'Importing...' : 'Import'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>}
        {!gcalLoading && gcalTravel.length === 0 && (
          <div style={{ padding:'16px 20px', textAlign:'center' }}>
            <p style={{ fontSize:12, color:'var(--text3)', margin:0 }}>
              Flights, hotels, and car services from your Google Calendar will appear here
            </p>
          </div>
        )}
      </Card>}

      {/* ALL TRAVEL — toggle panel */}
      {tab === 'all' && <Card style={{ padding:0, overflow:'hidden', borderTop:'2px solid rgba(30,64,175,0.4)' }}>
        <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>
            <i className="fa-solid fa-list" style={{ marginRight:5, fontSize:10 }}></i>ALL TRAVEL
          </p>
          <span style={{ fontSize:11, color:'var(--text3)', fontWeight:600 }}>{filtered.length}</span>
        </div>
        {/* Search */}
        <div style={{ padding:'10px 14px 8px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
          <i className="fa-solid fa-magnifying-glass" style={{ fontSize:13, color:'var(--text3)', flexShrink:0 }}></i>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search travel..."
            style={{ flex:1, background:'transparent', border:'none', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', padding:0 }} />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:16, padding:0, lineHeight:1, flexShrink:0 }}>×</button>
          )}
        </div>
        {/* Type filter pills */}
        <div style={{ padding:'8px 14px', borderBottom:'1px solid var(--border)', display:'flex', gap:6, flexWrap:'wrap' }}>
          {[{id:'all',label:'All',icon:''}, ...TRAVEL_TYPES].map(t => (
            <button key={t.id} onClick={()=>setFilter(t.id)}
              style={{ padding:'4px 10px', borderRadius:14, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:700,
                background:filter===t.id?'rgba(79,156,249,0.15)':'transparent', border:'1px solid '+(filter===t.id?'var(--blue)':'var(--border)'), color:filter===t.id?'var(--blue)':'var(--text3)' }}>
              {t.icon?<><i className={`fa-solid ${t.icon}`} style={{ marginRight:3, fontSize:9 }}></i></>:''}{t.label}
            </button>
          ))}
        </div>
        {loading ? <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>Loading...</p>
        : filtered.length === 0 ? <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>No travel records</p>
        : (() => {
          // Search filter
          const q = search.toLowerCase();
          const searchFiltered = q ? filtered.filter(r =>
            (r.airline||'').toLowerCase().includes(q) || (r.flightNumber||'').toLowerCase().includes(q) ||
            (r.hotelName||'').toLowerCase().includes(q) || (r.hotelAddress||'').toLowerCase().includes(q) ||
            (r.passenger||'').toLowerCase().includes(q) || (r.notes||'').toLowerCase().includes(q) ||
            (r.carServiceCompany||'').toLowerCase().includes(q) || (r.carServiceDriver||'').toLowerCase().includes(q) ||
            (r.driverName||'').toLowerCase().includes(q) || (r.trainCompany||'').toLowerCase().includes(q) ||
            (r.departureAirport||'').toLowerCase().includes(q) || (r.arrivalAirport||'').toLowerCase().includes(q) ||
            (r.carCompany||'').toLowerCase().includes(q)
          ) : filtered;
          if (q && searchFiltered.length === 0) return <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>No matches</p>;

          const tColors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
          const getTitle = r => r.type==='flight' ? `${r.airline||''} ${r.flightNumber||''}`.trim()||'Flight'
            : r.type==='hotel' ? r.hotelName||'Hotel'
            : r.type==='carservice' ? r.carServiceCompany||'Car Service'
            : r.type==='car' ? r.carCompany||'Car Rental'
            : r.type==='ground' ? r.driverName||'Ground'
            : r.trainCompany||'Train';
          const getSubtitle = r => r.type==='flight' ? `${r.departureAirport||'?'} → ${r.arrivalAirport||'?'}`
            : r.type==='hotel' ? r.hotelAddress?.split(',')[0]||''
            : r.type==='carservice' ? r.carServicePickup?.split(',')[0]||''
            : '';
          const getBarColor = r => {
            const pName = r.passenger || '';
            const pKey = pName && colorMap ? Object.keys(colorMap).find(k => k.toLowerCase() === pName.split(',')[0].trim().toLowerCase()) : null;
            return pKey ? colorMap[pKey] : tColors[r.type] || 'var(--border2)';
          };

          const renderRow = (r, i, total, isGrouped, count) => {
            const t = TRAVEL_TYPES.find(t => t.id===r.type);
            const isPast = r.endDate < today;
            const isOnDate = r.startDate <= selDate && r.endDate >= selDate;
            const title = getTitle(r);
            const subtitle = getSubtitle(r);
            const barColor = getBarColor(r);
            return (
              <div key={r.id} onClick={() => setViewRecord(r)}
                style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', cursor:'pointer',
                  borderBottom: i<total-1 ? '1px solid var(--border)' : 'none',
                  opacity: isGrouped ? 1 : (isPast ? 0.5 : 1), background: isOnDate ? 'rgba(79,156,249,0.04)' : 'transparent' }}
                onMouseEnter={el => el.currentTarget.style.background='rgba(79,156,249,0.06)'}
                onMouseLeave={el => el.currentTarget.style.background= isOnDate ? 'rgba(79,156,249,0.04)' : 'transparent'}>
                <div style={{ width:3, height:22, borderRadius:2, background: barColor, flexShrink:0 }} />
                <i className={`fa-solid ${t?.icon||'fa-circle'}`} style={{ fontSize:12, color: tColors[r.type], flexShrink:0 }}></i>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {title}{subtitle ? <span style={{ color:'var(--text3)', fontWeight:400 }}> · {subtitle}</span> : ''}
                  </p>
                  {r.passenger && isGrouped && <p style={{ fontSize:10, color:'var(--text3)', margin:'1px 0 0' }}>{r.passenger}</p>}
                </div>
                {count > 1 && (
                  <span style={{ fontSize:10, fontWeight:700, color:'var(--text3)', flexShrink:0, padding:'2px 6px',
                    background:'var(--bg)', borderRadius:8, border:'1px solid var(--border)' }}>
                    ×{count}
                  </span>
                )}
                <span style={{ fontSize:10, color:'var(--text3)', flexShrink:0, marginRight:4 }}>
                  {r.startDate ? new Date(r.startDate+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
                </span>
                {isOnDate ? (
                  <span style={{ width:22, height:22, borderRadius:'50%', background:'rgba(63,185,80,0.15)', border:'1px solid rgba(63,185,80,0.4)',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                    title="On selected date">
                    <i className="fa-solid fa-check" style={{ fontSize:9, color:'#3fb950' }}></i>
                  </span>
                ) : (
                  <button onClick={ev => { ev.stopPropagation(); handleDuplicate(r, selDate); }}
                    style={{ width:22, height:22, borderRadius:'50%', background:'var(--blue)', border:'none', cursor:'pointer', color:'white', fontSize:13,
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                    title={`Duplicate to ${selDate}`}>+</button>
                )}
                <button onClick={async (ev) => { ev.stopPropagation(); if (window.confirm('Delete this travel record and its events?')) { await api.deleteTravel(r.id); setRecords(prev => prev.filter(x => x.id !== r.id)); } }}
                  style={{ width:22, height:22, borderRadius:'50%', background:'rgba(248,81,73,0.1)', border:'1px solid rgba(248,81,73,0.2)', cursor:'pointer', color:'#f85149', fontSize:11, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                  title="Delete travel">×</button>
              </div>
            );
          };

          // When searching, group similar travel records
          if (q) {
            const groups = {};
            searchFiltered.forEach(r => {
              const key = `${r.type}::${getTitle(r).toLowerCase()}::${getSubtitle(r).toLowerCase()}::${(r.passenger||'').toLowerCase()}`;
              if (!groups[key]) groups[key] = [];
              groups[key].push(r);
            });
            const groupList = Object.values(groups).map(g => {
              g.sort((a,b) => (b.startDate||'').localeCompare(a.startDate||''));
              return g;
            }).sort((a,b) => (b[0].startDate||'').localeCompare(a[0].startDate||''));

            return (
              <div style={{ maxHeight:400, overflowY:'auto' }}>
                {groupList.map((group, gi) => {
                  const r = group[0];
                  const count = group.length;
                  const groupKey = `${r.type}::${getTitle(r)}::${getSubtitle(r)}::${r.passenger||''}`;
                  const isExpanded = expandedGroups.has(groupKey);
                  const t = TRAVEL_TYPES.find(t => t.id===r.type);
                  const isOnDate = r.startDate <= selDate && r.endDate >= selDate;
                  const title = getTitle(r);
                  const subtitle = getSubtitle(r);
                  const barColor = getBarColor(r);
                  return (
                    <div key={groupKey} style={{ borderBottom: gi<groupList.length-1 ? '1px solid var(--border)' : 'none' }}>
                      {/* Group header */}
                      <div onClick={() => setViewRecord(r)}
                        style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', cursor:'pointer' }}
                        onMouseEnter={el => el.currentTarget.style.background='rgba(79,156,249,0.04)'}
                        onMouseLeave={el => el.currentTarget.style.background='transparent'}>
                        <div style={{ width:3, height:22, borderRadius:2, background: barColor, flexShrink:0 }} />
                        <i className={`fa-solid ${t?.icon||'fa-circle'}`} style={{ fontSize:12, color: tColors[r.type], flexShrink:0 }}></i>
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {title}{subtitle ? <span style={{ color:'var(--text3)', fontWeight:400 }}> · {subtitle}</span> : ''}
                          </p>
                          {r.passenger && <p style={{ fontSize:10, color:'var(--text3)', margin:'1px 0 0' }}>{r.passenger}</p>}
                        </div>
                        {count > 1 && (
                          <button onClick={ev => { ev.stopPropagation(); setExpandedGroups(prev => { const n = new Set(prev); if (n.has(groupKey)) n.delete(groupKey); else n.add(groupKey); return n; }); }}
                            style={{ fontSize:10, fontWeight:700, color: isExpanded ? 'var(--blue)' : 'var(--text3)', flexShrink:0, padding:'2px 8px',
                              background: isExpanded ? 'rgba(79,156,249,0.1)' : 'var(--bg)', borderRadius:8, border:'1px solid '+(isExpanded ? 'var(--blue)' : 'var(--border)'),
                              cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', display:'flex', alignItems:'center', gap:3 }}>
                            ×{count}
                            <i className={`fa-solid fa-chevron-${isExpanded?'up':'down'}`} style={{ fontSize:7 }}></i>
                          </button>
                        )}
                        <span style={{ fontSize:10, color:'var(--text3)', flexShrink:0, marginRight:4 }}>
                          {r.startDate ? new Date(r.startDate+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
                        </span>
                        {isOnDate ? (
                          <span style={{ width:22, height:22, borderRadius:'50%', background:'rgba(63,185,80,0.15)', border:'1px solid rgba(63,185,80,0.4)',
                            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <i className="fa-solid fa-check" style={{ fontSize:9, color:'#3fb950' }}></i>
                          </span>
                        ) : (
                          <button onClick={ev => { ev.stopPropagation(); handleDuplicate(r, selDate); }}
                            style={{ width:22, height:22, borderRadius:'50%', background:'var(--blue)', border:'none', cursor:'pointer', color:'white', fontSize:13,
                              display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                            title={`Duplicate to ${selDate}`}>+</button>
                        )}
                      </div>
                      {/* Expanded: show all occurrences, most recent first */}
                      {isExpanded && count > 1 && (
                        <div style={{ background:'var(--bg)', borderTop:'1px solid var(--border)' }}>
                          {group.map((sub, si) => renderRow(sub, si, group.length, false, 1))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }

          // Normal view — flat list sorted by date
          const allSorted = [...searchFiltered].sort((a, b) => {
            const aOnDate = a.startDate <= selDate && a.endDate >= selDate;
            const bOnDate = b.startDate <= selDate && b.endDate >= selDate;
            if (aOnDate && !bOnDate) return -1;
            if (!aOnDate && bOnDate) return 1;
            return b.startDate.localeCompare(a.startDate);
          });
          return (
            <div style={{ maxHeight:400, overflowY:'auto' }}>
              {allSorted.map((r, i) => renderRow(r, i, allSorted.length, false, 1))}
            </div>
          );
        })()}
      </Card>}
      {modal && <TravelFormModal record={modal==='new'?null:modal} artistId={selArtist} artists={artists} onSave={handleSave} onClose={()=>setModal(null)} />}
      {viewRecord && <TravelDetailModal record={viewRecord} onClose={() => setViewRecord(null)} />}
      {ConfirmDialog}
    </div>
  );
}
